import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AppLink, useGo } from '../components/Nav';
import { ArrowLeftIcon, PlusIcon, ScissorsIcon, TrashIcon } from '../components/icons';
import { Banner, Button, ErrorText, Loading, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { paywallPath } from '../lib/plans';
import { ApiError, errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import { loadWaveform } from '../player/waveform';
import { usePlanPlayer } from '../player/usePlanPlayer';
import type { SharePayload } from '../lib/types';
import {
  applyEdit,
  fetchClipSource,
  fetchEditState,
  type Clip,
  type ClipSource,
  type EditState,
  type Piece,
  type Range,
  type Word,
} from '../queries/edit';
import { qk } from '../queries/keys';
import {
  MAX_CLIPS,
  MIN_PIECE_SEC,
  clipCount,
  forApi,
  isWhole,
  movePiece,
  newId,
  pieceId,
  pieceLength,
  piecesOf,
  plannedDuration,
  removePiece,
  selfRanges,
  splitPiece,
  subtractRange,
  trimBounds,
  trimPiece,
  type Placed,
} from '../lib/timeline';
import { ClipPicker } from './EditClips';

/**
 * The editor.
 *
 * One timeline: the finished video, piece by piece, in the order it plays.
 * A piece is a range of the original recording or a range of another
 * recording dropped in as a clip, and every piece is handled the same way:
 * split it at the playhead, delete it, drag it to another position, drag
 * its edges to trim. The video above plays the pieces in that order, and
 * the transcript on the right is the original's words with their times,
 * struck through where nothing plays them.
 *
 * The waveform behind the pieces and the transcript both belong to the
 * original, and each piece of the original draws its own slice of them;
 * a clip draws its poster instead. "Finish" sends the list to the API,
 * which renders it and regenerates the captions. Nothing is destructive
 * until then, and even then the original is kept, as is every recording a
 * clip was taken from.
 */

/** Everything an undo has to put back. */
interface Snapshot {
  pieces: Piece[];
  words: Word[];
}

/** True when nothing plays this second of the original. */
function isCut(time: number, keep: Range[]): boolean {
  return !keep.some(([s, e]) => time >= s && time < e);
}

/** The pieces as the older API shape, sent alongside for a build that predates ordered pieces. */
function legacyOf(pieces: Piece[], duration: number): { keep: Range[] | null; clips: Clip[] } {
  const keep = isWhole(pieces, duration) ? null : selfRanges(pieces);
  const clips: Clip[] = [];
  pieces.forEach((piece, index) => {
    if (piece.kind !== 'clip') return;
    const after = pieces.slice(index + 1).find((one) => one.kind === 'self');
    clips.push({ id: piece.id, videoId: piece.videoId, at: after ? after.start : duration, start: piece.start, end: piece.end });
  });
  return { keep, clips };
}

export function EditPage({ videoId }: { videoId: string }) {
  const go = useGo();
  const state = useQuery({
    queryKey: ['edit', videoId],
    queryFn: () => fetchEditState(videoId),
    retry: 1,
  });

  if (state.isPending) return <Loading label="Opening the editor" />;
  if (state.isError) {
    const upsell = state.error instanceof ApiError && state.error.isQuota;
    return (
      <div className="mx-auto max-w-md py-16">
        {upsell ? (
          <Banner
            tone="info"
            title="Editing comes with a paid seat"
            action={
              <AppLink to={paywallPath('editing')}>
                <Button size="sm" variant="primary">
                  Upgrade
                </Button>
              </AppLink>
            }
          >
            Cut, trim and correct the transcript of anything you record, on the Team plan. The
            recording itself is untouched and still yours.
          </Banner>
        ) : (
          <Banner tone="danger" title="Could not open this recording for editing">
            {errorMessage(state.error)}
          </Banner>
        )}
      </div>
    );
  }
  const data = state.data;
  if (data.status !== 'ready') {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <Spinner size={22} className="mx-auto text-chalk-faint" />
        <h1 className="mt-4 text-h3 text-chalk">Still processing</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Editing opens up once the recording is ready. This usually takes a minute or two.
        </p>
        <AppLink to={`/v/${data.shareId}`} className="mt-5 inline-block">
          <Button>Back to the recording</Button>
        </AppLink>
      </div>
    );
  }
  return <Editor initial={data} onDone={() => go(`/v/${data.shareId}`)} />;
}

function Editor({ initial, onDone }: { initial: EditState; onDone: () => void }) {
  const duration = initial.sourceDurationSec;

  useEffect(() => {
    track('Edit Started', { video_id: initial.shareId });
  }, [initial.shareId]);

  // ---- state and history ---------------------------------------------------
  const [initialPieces] = useState<Piece[]>(() => piecesOf(initial, duration));
  const [pieces, setPieces] = useState<Piece[]>(initialPieces);
  const [words, setWords] = useState<Word[]>(initial.words ?? []);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [future, setFuture] = useState<Snapshot[]>([]);
  const dragBaseline = useRef<Snapshot | null>(null);
  const here = (): Snapshot => ({ pieces, words });
  const commit = useCallback(
    (next: { pieces?: Piece[]; words?: Word[] }) => {
      // An explicit action owns its history entry; a drag in flight does not
      // get to add another at mouse-up.
      dragBaseline.current = null;
      setHistory((h) => [...h, { pieces, words }].slice(-100));
      setFuture([]);
      if (next.pieces) setPieces(next.pieces);
      if (next.words) setWords(next.words);
    },
    [pieces, words],
  );
  const restoreSnapshot = (snapshot: Snapshot) => {
    setPieces(snapshot.pieces);
    setWords(snapshot.words);
  };
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setFuture((f) => [here(), ...f]);
    setHistory((h) => h.slice(0, -1));
    restoreSnapshot(last);
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setHistory((h) => [...h, here()]);
    setFuture((f) => f.slice(1));
    restoreSnapshot(next);
  };
  /** A drag: the edit changes on every move, and the release is one undo. */
  const dragPieces = (next: Piece[]) => {
    if (!dragBaseline.current) dragBaseline.current = { pieces, words };
    setPieces(next);
  };
  useEffect(() => {
    const up = () => {
      const base = dragBaseline.current;
      dragBaseline.current = null;
      if (!base) return;
      if (JSON.stringify(base.pieces) !== JSON.stringify(pieces)) {
        setHistory((h) => [...h, base].slice(-100));
        setFuture([]);
      }
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [pieces]);
  const wordsChanged = JSON.stringify(words) !== JSON.stringify(initial.words ?? []);
  const dirty = JSON.stringify(forApi(pieces)) !== JSON.stringify(forApi(initialPieces)) || wordsChanged;

  // ---- the clips' recordings -----------------------------------------------
  /**
   * The sources start as whatever the API sent with the edit, and grow as
   * clips are added. Kept past a removal on purpose: an undo brings the clip
   * back and there is no reason to fetch its poster a second time.
   */
  const [sources, setSources] = useState<Map<string, ClipSource>>(
    () => new Map((initial.clipSources ?? []).map((source) => [source.videoId, source])),
  );
  const [picking, setPicking] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const lengthOf = (piece: Piece) => (piece.kind === 'clip' ? (sources.get(piece.videoId)?.durationSec ?? null) : duration);

  // ---- playback -----------------------------------------------------------------
  const player = usePlanPlayer({ pieces });
  const { plan, total, outTime, sourceTime, playing, current, activeClip, clipTime, toggle, seekOut, seekSource } = player;
  const [videoReady, setVideoReady] = useState(false);

  // ---- what the hand is on ------------------------------------------------------
  /**
   * The piece being worked on: the one clicked, else the one under the
   * playhead. A range dragged out in the transcript is a selection of the
   * original instead, and Delete takes that out of whichever pieces play it.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? (plan.find((one) => pieceId(one.piece) === selectedId) ?? null) : null;
  const target: Placed | null = selected ?? current;
  const [selection, setSelection] = useState<Range | null>(null);
  const keep = useMemo(() => selfRanges(pieces), [pieces]);
  /** The current piece's range of the original, for the transcript to mark. */
  const segment: Range | null =
    pieces.length > 1 && current && current.piece.kind === 'self' ? [current.piece.start, current.piece.end] : null;

  const addClip = async (videoId: string) => {
    setPicking(false);
    setPickError(null);
    try {
      const source = await fetchClipSource(videoId);
      if (!source.available || !source.durationSec) {
        setPickError(`“${source.title}” cannot be played, so it cannot be dropped in.`);
        return;
      }
      setSources((map) => new Map(map).set(source.videoId, source));
      // Onto the end of the timeline, where the button sits; drag it from there.
      const clip: Piece = {
        kind: 'clip',
        id: newId('c'),
        videoId: source.videoId,
        start: 0,
        end: Math.min(source.durationSec, 30),
      };
      commit({ pieces: [...pieces, clip] });
      setSelectedId(clip.id);
      seekOut(plannedDuration(plan));
      track('Clip Added', { video_id: initial.shareId, clip_video_id: source.videoId });
    } catch (error) {
      setPickError(errorMessage(error));
    }
  };

  // ---- actions ---------------------------------------------------------------
  /** Where a split would fall: a second inside the current piece's source. */
  const splitAt = current ? (current.piece.kind === 'clip' ? clipTime : sourceTime) : null;
  const canSplit =
    current !== null &&
    splitAt !== null &&
    splitAt > current.piece.start + MIN_PIECE_SEC &&
    splitAt < current.piece.end - MIN_PIECE_SEC;
  const deletable = selection !== null ? subtractRange(pieces, selection) : target ? removePiece(pieces, target.index) : null;
  const canDelete = deletable !== null;

  const splitHere = () => {
    if (!current || splitAt === null || !canSplit) return;
    commit({ pieces: splitPiece(pieces, current.index, splitAt) });
    setSelection(null);
    setSelectedId(null);
  };
  const deleteTarget = () => {
    if (!deletable) return;
    commit({ pieces: deletable });
    setSelection(null);
    setSelectedId(null);
  };
  const trim = (index: number, edge: 'start' | 'end', to: number) => {
    const piece = pieces[index];
    if (!piece) return;
    dragPieces(trimPiece(pieces, index, edge, to, trimBounds(pieces, index, duration, lengthOf(piece))));
  };
  const move = (from: number, to: number) => dragPieces(movePiece(pieces, from, to));

  // Keyboard: space plays, S splits, delete removes the piece or the
  // selection, cmd/ctrl-z undoes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        deleteTarget();
      } else if (event.key.toLowerCase() === 's' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        splitHere();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (event.key === 'Escape') {
        setSelection(null);
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---- finish ------------------------------------------------------------------
  const queryClient = useQueryClient();
  const finish = useMutation({
    mutationFn: () =>
      applyEdit(initial.videoId, {
        pieces: forApi(pieces),
        // The older shape rides along for an API build that predates pieces.
        ...legacyOf(pieces, duration),
        // Only words that were touched. A recording with no transcript has
        // an empty list here, and sending it would ask for captions of nothing.
        words: wordsChanged ? words : undefined,
      }),
    onSuccess: (result) => {
      track('Edit Applied', {
        video_id: initial.shareId,
        pieces: pieces.length,
        clips: clipCount(pieces),
      });
      // The share page plays this edit itself while it renders, from this
      // very state. Written into the cache on the way out so it is there
      // the instant the page comes up, and so the page's first poll does
      // not show the previous version for a beat before the flag lands.
      queryClient.setQueryData<EditState>(['edit', initial.videoId], (old) =>
        old
          ? {
              ...old,
              pieces: forApi(pieces),
              ...legacyOf(pieces, duration),
              words: wordsChanged ? words : old.words,
              version: result.version,
              rendering: result.rendering,
            }
          : old,
      );
      if (result.rendering) {
        queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(initial.shareId) }, (old) =>
          old ? { ...old, rendering: true } : old,
        );
      }
      onDone();
    },
  });

  const activeSource = activeClip && activeClip.piece.kind === 'clip' ? sources.get(activeClip.piece.videoId) : undefined;

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-chalk">
      <header className="shrink-0 border-b border-hairline">
        <div className="mx-auto flex h-12 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <AppLink to={`/v/${initial.shareId}`} aria-label="Back" className="text-chalk-dim hover:text-chalk">
            <ArrowLeftIcon size={18} />
          </AppLink>
          <h1 className="min-w-0 flex-1 truncate text-body text-chalk">{initial.title}</h1>
          <span className="hidden text-caption text-chalk-faint sm:inline">
            {dirty ? 'Unsaved changes' : 'No changes'} · will run {timecode(total)}
          </span>
          <button
            type="button"
            onClick={undo}
            disabled={history.length === 0}
            className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={future.length === 0}
            className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
          >
            Redo
          </button>
          <Button variant="primary" size="sm" busy={finish.isPending} disabled={!dirty} onClick={() => finish.mutate()}>
            Finish
          </Button>
        </div>
      </header>

      <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col px-4 sm:px-6">
        {finish.isError ? (
          <div className="pt-3">
            <ErrorText>Could not save this edit. {errorMessage(finish.error)}</ErrorText>
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 gap-4 pt-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-md border border-hairline bg-black" data-theme="dark">
              <video
                ref={player.videoRef}
                src={initial.sourceUrl}
                className="max-h-full max-w-full"
                onClick={toggle}
                onCanPlay={() => setVideoReady(true)}
                playsInline
                preload="auto"
              />
              {activeClip && activeClip.piece.kind === 'clip' ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <video
                    ref={player.clipVideoRef}
                    key={pieceId(activeClip.piece)}
                    src={activeSource?.url ?? undefined}
                    className="max-h-full max-w-full"
                    onClick={toggle}
                    playsInline
                    preload="auto"
                    {...player.clipHandlers}
                  />
                  <div className="pointer-events-none absolute left-3 top-3 rounded bg-slate-950/80 px-2 py-1 text-caption text-chalk">
                    {activeSource?.title ?? 'Clip'}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex h-10 shrink-0 items-center gap-3 px-1 text-caption">
              <button type="button" onClick={toggle} className="text-chalk hover:text-chalk">
                {playing ? 'Pause' : 'Play'}
              </button>
              <span className="font-mono tnum text-chalk-dim">
                {timecode(outTime)} / {timecode(total)}
              </span>
              {current?.piece.kind === 'self' ? (
                <span className="font-mono tnum text-chalk-faint">· {timecode(sourceTime)} of the original</span>
              ) : activeSource ? (
                <span className="truncate text-chalk-faint">· clip from {activeSource.title}</span>
              ) : null}
              <span className="flex-1" />
              <span className="hidden text-chalk-faint lg:inline">Space plays · S splits · ⌫ deletes · ⌘Z undoes</span>
            </div>
          </div>

          <aside className="w-[320px] shrink-0 overflow-y-auto rounded-md border border-hairline px-4 py-4">
            <TranscriptEditor
              words={words}
              keep={keep}
              time={sourceTime}
              status={initial.transcriptStatus}
              selection={selection}
              segment={selection ? null : segment}
              onSeek={seekSource}
              onSelect={setSelection}
              onChangeWord={(index, text) => commit({ words: words.map((w, i) => (i === index ? { ...w, w: text } : w)) })}
              onReplaceSpan={(from, to, replacement) =>
                commit({ words: [...words.slice(0, from), ...replacement, ...words.slice(to)] })
              }
            />
          </aside>
        </div>

        <Timeline
          plan={plan}
          total={total}
          duration={duration}
          // The raw upload streams as fragments and is read first; the
          // render is the fallback once the raw has expired.
          waveformUrls={[initial.waveformUrl, initial.sourceUrl].filter((url): url is string => Boolean(url))}
          sources={sources}
          outTime={outTime}
          selectedId={selectedId}
          currentId={current ? pieceId(current.piece) : null}
          waveformReady={videoReady}
          onSeekOut={seekOut}
          onSelect={(id) => {
            setSelectedId(id);
            setSelection(null);
          }}
          onMove={move}
          onTrim={trim}
          // Nothing to stitch into a voice note: every clip is a picture.
          onAdd={initial.kind === 'audio' ? null : () => setPicking(true)}
          addDisabled={clipCount(pieces) >= MAX_CLIPS}
          tools={
            <EditTools
              selection={selection}
              target={target}
              sources={sources}
              canSplit={canSplit}
              canDelete={canDelete}
              onSplit={splitHere}
              onDelete={deleteTarget}
              onClear={() => {
                setSelection(null);
                setSelectedId(null);
              }}
            />
          }
        >
          {pickError ? <ErrorText>{pickError}</ErrorText> : null}
        </Timeline>
      </div>

      {picking ? (
        <ClipPicker excludeVideoId={initial.videoId} onPick={(video) => void addClip(video.id)} onClose={() => setPicking(false)} />
      ) : null}
    </div>
  );
}

/**
 * The controls for what the hand is on, over the timeline where the hand
 * already is. What they read depends on it: a range dragged out in the
 * transcript deletes as a range, a piece as a piece.
 */
function EditTools({
  selection,
  target,
  sources,
  canSplit,
  canDelete,
  onSplit,
  onDelete,
  onClear,
}: {
  selection: Range | null;
  target: Placed | null;
  sources: Map<string, ClipSource>;
  canSplit: boolean;
  canDelete: boolean;
  onSplit: () => void;
  onDelete: () => void;
  onClear: () => void;
}) {
  const span = (range: Range) => `${timecode(range[0])} to ${timecode(range[1])}`;
  const name =
    target === null
      ? ''
      : target.piece.kind === 'clip'
        ? `clip “${sources.get(target.piece.videoId)?.title ?? 'Clip'}”`
        : `piece ${span([target.piece.start, target.piece.end])}`;
  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        onClick={onSplit}
        disabled={!canSplit}
        title={canSplit ? 'Split the piece at the playhead (S)' : 'Move the playhead inside a piece to split it'}
      >
        <ScissorsIcon size={13} />
        Split
      </Button>
      <Button
        size="sm"
        variant="danger"
        onClick={onDelete}
        disabled={!canDelete}
        title={
          selection
            ? 'Take the selected range out (Delete)'
            : target && !canDelete
              ? 'Split the recording first, then delete a piece of it'
              : 'Take this piece out (Delete)'
        }
      >
        <TrashIcon size={13} />
        {selection ? `Delete ${span(selection)}` : target ? `Delete ${name}` : 'Delete'}
      </Button>
      {selection ? (
        <button type="button" onClick={onClear} className="text-caption text-chalk-faint hover:text-chalk">
          Clear selection
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transcript: words in lines. Click a word to go there, which makes its clip
// the current one; drag across words to select a range; double-click a word
// to fix it, or edit a whole line as
// text: its words are re-spread over the line's time, one to one when the
// count matches and evenly when it does not. Cut words are struck through.

interface Line {
  from: number; // index of first word
  to: number; // index after last word
}

/** Lines break at sentence ends, pauses, and about a dozen words. */
function linesOf(words: Word[]): Line[] {
  const lines: Line[] = [];
  let from = 0;
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const next = words[i + 1];
    const count = i - from + 1;
    const breakHere = !next || count >= 14 || /[.?!]$/.test(word.w) || next.s - word.e > 1.0;
    if (breakHere) {
      lines.push({ from, to: i + 1 });
      from = i + 1;
    }
  }
  return lines;
}

/** New text for a span of words, keeping the span's timing. */
function retime(old: Word[], text: string): Word[] {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  if (tokens.length === old.length) return old.map((word, i) => ({ ...word, w: tokens[i] }));
  const start = old[0].s;
  const end = old[old.length - 1].e;
  const each = (end - start) / tokens.length;
  return tokens.map((w, i) => ({ s: start + i * each, e: start + (i + 1) * each, w }));
}

function TranscriptEditor({
  words,
  keep,
  time,
  status,
  selection,
  segment,
  onSeek,
  onSelect,
  onChangeWord,
  onReplaceSpan,
}: {
  words: Word[];
  keep: Range[];
  time: number;
  status: 'pending' | 'ready' | 'failed' | null;
  selection: Range | null;
  /** The clip under the playhead, when nothing is dragged out. */
  segment: Range | null;
  onSeek: (at: number) => void;
  onSelect: (range: Range | null) => void;
  onChangeWord: (index: number, text: string) => void;
  onReplaceSpan: (from: number, to: number, replacement: Word[]) => void;
}) {
  const anchor = useRef<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [lineDraft, setLineDraft] = useState('');
  useEffect(() => {
    const clear = () => {
      anchor.current = null;
    };
    window.addEventListener('mouseup', clear);
    return () => window.removeEventListener('mouseup', clear);
  }, []);
  const lines = useMemo(() => linesOf(words), [words]);

  if (status !== 'ready' || words.length === 0) {
    return (
      <div>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 text-body text-chalk-dim">
          {status === 'pending'
            ? 'Still listening. Cuts work now; the words arrive in a minute or two.'
            : 'No transcript for this recording. Cuts still work from the timeline.'}
        </p>
      </div>
    );
  }

  const inSelection = (w: Word) => selection !== null && w.e > selection[0] && w.s < selection[1];
  // The current clip's words are marked only once the track has more than
  // one clip; before the first split the whole transcript is the clip.
  const inSegment = (w: Word) =>
    keep.length > 1 && segment !== null && w.s >= segment[0] && w.s < segment[1];

  return (
    <div className="select-none">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <span className="text-right text-caption text-chalk-faint">
          Click to go there · drag to select · double-click fixes a word
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {lines.map((line, lineIndex) => {
          const lineWords = words.slice(line.from, line.to);
          const live = time >= lineWords[0].s && time < lineWords[lineWords.length - 1].e;
          if (editingLine === lineIndex) {
            return (
              <div key={lineIndex} className="rounded-md border border-chalk/30 bg-slate-900 p-2">
                <textarea
                  autoFocus
                  value={lineDraft}
                  rows={3}
                  onChange={(event) => setLineDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setEditingLine(null);
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      onReplaceSpan(line.from, line.to, retime(lineWords, lineDraft));
                      setEditingLine(null);
                    }
                  }}
                  className="w-full resize-none bg-transparent text-body text-chalk outline-none"
                />
                <div className="mt-1 flex items-center justify-between text-caption text-chalk-faint">
                  <span>
                    {timecode(lineWords[0].s)} to {timecode(lineWords[lineWords.length - 1].e)} ·
                    Enter saves, Esc cancels
                  </span>
                  <span>
                    <button
                      type="button"
                      className="hover:text-chalk"
                      onClick={() => setEditingLine(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ml-3 text-chalk hover:underline"
                      onClick={() => {
                        onReplaceSpan(line.from, line.to, retime(lineWords, lineDraft));
                        setEditingLine(null);
                      }}
                    >
                      Save
                    </button>
                  </span>
                </div>
              </div>
            );
          }
          return (
            <div
              key={lineIndex}
              className={cx('group flex gap-2 rounded-md px-1 py-0.5', live && 'bg-slate-900/60')}
            >
              <button
                type="button"
                onClick={() => onSeek(lineWords[0].s)}
                className="w-11 shrink-0 pt-1 text-left font-mono text-caption text-signal"
              >
                {timecode(lineWords[0].s)}
              </button>
              <p className="min-w-0 flex-1 leading-7 text-body">
                {lineWords.map((word, offset) => {
                  const index = line.from + offset;
                  const cut = isCut(word.s, keep);
                  const spoken = time >= word.s && time < word.e;
                  const selected = inSelection(word);
                  if (editing === index) {
                    return (
                      <input
                        key={index}
                        autoFocus
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onBlur={() => {
                          if (draft.trim() && draft.trim() !== word.w)
                            onChangeWord(index, draft.trim());
                          setEditing(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                          if (event.key === 'Escape') setEditing(null);
                        }}
                        className="mx-0.5 inline-block w-28 rounded-xs border border-chalk/30 bg-slate-900 px-1 text-body text-chalk"
                      />
                    );
                  }
                  return (
                    <span
                      key={index}
                      onMouseDown={(event) => {
                        if (event.detail === 2) return;
                        anchor.current = index;
                      }}
                      onMouseEnter={() => {
                        const from = anchor.current;
                        if (from === null || from === index) return;
                        const a = words[Math.min(from, index)];
                        const b = words[Math.max(from, index)];
                        onSelect([a.s, b.e]);
                      }}
                      onMouseUp={() => {
                        // A click, not a drag: go there. The clip under the
                        // word becomes the current one.
                        if (anchor.current === index) {
                          onSelect(null);
                          onSeek(word.s);
                        }
                        anchor.current = null;
                      }}
                      onDoubleClick={() => {
                        setDraft(word.w);
                        setEditing(index);
                      }}
                      className={cx(
                        'cursor-pointer rounded-xs px-0.5 transition-colors',
                        cut && 'text-chalk-faint line-through',
                        !cut &&
                          !selected &&
                          (spoken ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk'),
                        !cut && !selected && !spoken && inSegment(word) && 'bg-slate-800/70 text-chalk',
                        selected && 'bg-record/40 text-chalk',
                      )}
                    >
                      {word.w}{' '}
                    </span>
                  );
                })}
              </p>
              <button
                type="button"
                aria-label="Edit this line"
                title="Edit this line"
                onClick={() => {
                  setLineDraft(lineWords.map((w) => w.w).join(' '));
                  setEditingLine(lineIndex);
                }}
                className="shrink-0 self-start pt-1 text-caption text-chalk-faint opacity-0 transition-opacity hover:text-chalk group-hover:opacity-100"
              >
                ✎
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline: the finished video as a row of pieces, in the order they play.
// Click a piece to put the playhead there and make it the one being worked
// on; drag a piece to move it; drag its edges to trim it; drag along the
// ruler to scrub. Zoom with the slider or pinch, scroll sideways when zoomed
// in. The square at the right end adds a clip onto the tail.

type TimelineDrag =
  | { kind: 'move'; id: string; grabX: number; moved: boolean; outAtGrab: number }
  | { kind: 'trim'; id: string; edge: 'start' | 'end' }
  | { kind: 'scrub' }
  | null;

function Timeline({
  plan,
  total,
  duration,
  waveformUrls,
  sources,
  outTime,
  selectedId,
  currentId,
  waveformReady,
  onSeekOut,
  onSelect,
  onMove,
  onTrim,
  onAdd,
  addDisabled,
  tools,
  children,
}: {
  plan: Placed[];
  total: number;
  duration: number;
  /** Where to read the waveform from, in order of preference. */
  waveformUrls: string[];
  sources: Map<string, ClipSource>;
  outTime: number;
  selectedId: string | null;
  /** The piece under the playhead. */
  currentId: string | null;
  /** The player has its first frames: only then is the waveform worth fetching. */
  waveformReady: boolean;
  onSeekOut: (out: number) => void;
  onSelect: (id: string | null) => void;
  onMove: (from: number, to: number) => void;
  onTrim: (index: number, edge: 'start' | 'end', to: number) => void;
  /** Null for a recording nothing can be dropped into. */
  onAdd: (() => void) | null;
  addDisabled: boolean;
  /** Split and delete: the editor's own controls, in this panel's header. */
  tools: ReactNode;
  children?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [covered, setCovered] = useState(0);
  const [waveDone, setWaveDone] = useState(false);
  const drag = useRef<TimelineDrag>(null);
  const planRef = useRef(plan);
  planRef.current = plan;

  const safeTotal = Math.max(total, 0.001);
  const outAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.max(0, Math.min(total, ((clientX - rect.left) / rect.width) * total));
  };
  const left = (out: number) => `${(out / safeTotal) * 100}%`;
  const width = (seconds: number) => `${(seconds / safeTotal) * 100}%`;

  /**
   * The waveform, read from the original in the background. It waits for the
   * player to have its first frames: both come from the same file, and the
   * picture matters more than the shape of the sound behind it. Somebody who
   * refreshed used to sit in front of a black player while this read the
   * whole recording.
   */
  const waveformKey = waveformUrls.join('\n');
  useEffect(() => {
    if (!waveformReady) return;
    const controller = new AbortController();
    setPeaks(null);
    setCovered(0);
    setWaveDone(false);
    const urls = waveformKey ? waveformKey.split('\n') : [];
    void (async () => {
      // The first source that reads; a raw upload the bucket has let go
      // fails at the fetch and the render is tried instead.
      for (const url of urls) {
        if (controller.signal.aborted) return;
        try {
          await loadWaveform(
            url,
            duration,
            (progress) => {
              setPeaks(new Float32Array(progress.peaks));
              setCovered(progress.coveredSec);
              if (progress.done) setWaveDone(true);
            },
            controller.signal,
          );
          return;
        } catch {
          // Next one.
        }
      }
      setWaveDone(true);
    })();
    return () => controller.abort();
  }, [waveformKey, duration, waveformReady]);

  // Each piece of the original draws its own slice of the waveform, where
  // it sits in the finished video. Drawn at the zoomed width so bars stay
  // one per column.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * devicePixelRatio));
    canvas.height = Math.max(1, Math.floor(rect.height * devicePixelRatio));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!peaks || total <= 0) return;
    const ink = getComputedStyle(canvas).getPropertyValue('--c-ink-dim').trim() || '155 167 180';
    ctx.fillStyle = `rgb(${ink} / 0.55)`;
    const mid = canvas.height / 2;
    const secondsPerBucket = duration / peaks.length;
    const column = Math.max(1, Math.floor(2 * devicePixelRatio));
    for (const placed of plan) {
      if (placed.piece.kind !== 'self') continue;
      const x0 = (placed.outStart / total) * canvas.width;
      const x1 = (placed.outEnd / total) * canvas.width;
      const seconds = pieceLength(placed.piece);
      for (let x = x0; x < x1; x += column) {
        const t0 = placed.piece.start + ((x - x0) / (x1 - x0)) * seconds;
        const t1 = placed.piece.start + ((x + column - x0) / (x1 - x0)) * seconds;
        const b0 = Math.max(0, Math.floor(t0 / secondsPerBucket));
        const b1 = Math.min(peaks.length - 1, Math.max(b0, Math.floor(t1 / secondsPerBucket)));
        let peak = 0;
        for (let b = b0; b <= b1; b += 1) if (peaks[b] > peak) peak = peaks[b];
        const h = Math.max(1, peak * canvas.height * 0.9);
        ctx.fillRect(x, mid - h / 2, Math.max(1, column - 0.5), h);
      }
    }
  }, [peaks, zoom, plan, total, duration]);

  // Keep the playhead in view when zoomed in and playing.
  useEffect(() => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    if (!scroller || !track || zoom === 1 || total <= 0) return;
    const x = (outTime / total) * track.clientWidth;
    if (x < scroller.scrollLeft + 40 || x > scroller.scrollLeft + scroller.clientWidth - 40) {
      scroller.scrollLeft = Math.max(0, x - scroller.clientWidth / 3);
    }
  }, [outTime, zoom, total]);

  const ticks = useMemo(() => {
    const visibleSec = total / zoom;
    const step =
      visibleSec <= 30 ? 1 : visibleSec <= 90 ? 5 : visibleSec <= 300 ? 15 : visibleSec <= 900 ? 60 : visibleSec <= 3600 ? 300 : 600;
    const out: number[] = [];
    for (let t = 0; t <= total; t += step) out.push(t);
    return out;
  }, [total, zoom]);

  /**
   * Where a dragged piece wants to go: the position among the others whose
   * midpoint the cursor has passed. Worked out without the moving piece, so
   * the point under the cursor does not slide about as the piece's own width
   * gets out of the way ahead of it.
   */
  const dropIndex = (id: string, clientX: number): number => {
    const others = planRef.current.filter((one) => pieceId(one.piece) !== id);
    const out = outAt(clientX);
    let cursor = 0;
    for (let i = 0; i < others.length; i += 1) {
      const seconds = others[i].outEnd - others[i].outStart;
      if (out < cursor + seconds / 2) return i;
      cursor += seconds;
    }
    return others.length;
  };

  useEffect(() => {
    const move = (event: MouseEvent) => {
      const state = drag.current;
      if (!state) return;
      event.preventDefault();
      if (state.kind === 'scrub') {
        onSeekOut(outAt(event.clientX));
      } else if (state.kind === 'move') {
        if (!state.moved && Math.abs(event.clientX - state.grabX) < 6) return;
        state.moved = true;
        const from = planRef.current.findIndex((one) => pieceId(one.piece) === state.id);
        if (from < 0) return;
        const to = dropIndex(state.id, event.clientX);
        if (to !== from) onMove(from, to);
      } else {
        const placed = planRef.current.find((one) => pieceId(one.piece) === state.id);
        if (!placed) return;
        // A trim handle moves in the finished video's seconds, and a second
        // there is a second inside the source: same scale, no conversion.
        const anchor = state.edge === 'start' ? placed.outStart : placed.outEnd;
        const delta = outAt(event.clientX) - anchor;
        onTrim(placed.index, state.edge, (state.edge === 'start' ? placed.piece.start : placed.piece.end) + delta);
      }
    };
    const up = (event: MouseEvent) => {
      const state = drag.current;
      drag.current = null;
      if (!state) return;
      if (state.kind === 'move' && !state.moved) {
        // A press with no drag: the playhead goes where the press was, and
        // the piece under it is the one being worked on.
        onSelect(state.id);
        onSeekOut(outAt(event.clientX));
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  });

  const setZoomKeepingCentre = (next: number) => {
    const scroller = scrollRef.current;
    const clamped = Math.max(1, Math.min(40, next));
    if (scroller) {
      const centre = (scroller.scrollLeft + scroller.clientWidth / 2) / (scroller.clientWidth * zoom);
      setZoom(clamped);
      requestAnimationFrame(() => {
        scroller.scrollLeft = centre * scroller.clientWidth * clamped - scroller.clientWidth / 2;
      });
    } else {
      setZoom(clamped);
    }
  };

  const pieceCount = plan.length;
  const clips = plan.filter((one) => one.piece.kind === 'clip').length;
  const hint =
    pieceCount <= 1
      ? 'One piece · put the playhead where the cut goes and press Split, then delete what goes'
      : `${pieceCount} pieces${clips ? `, ${clips} of them clips` : ''} · drag a piece to move it · drag its edges to trim it`;

  return (
    <div className="shrink-0 border-t border-hairline pb-4 pt-3">
      <div className="mb-2 flex items-center gap-4 text-caption">
        {tools}
        <span className="flex-1" />
        <span className="hidden truncate text-chalk-faint lg:inline">
          {hint}
          {!waveDone && waveformReady ? <span className="text-chalk-faint/70"> · reading audio{peaks ? ` ${Math.round((covered / duration) * 100)}%` : '…'}</span> : null}
        </span>
        <label className="flex items-center gap-2 text-chalk-faint">
          <button type="button" onClick={() => setZoomKeepingCentre(1)} className={cx('hover:text-chalk', zoom === 1 && 'text-chalk')}>
            Fit
          </button>
          <input
            type="range"
            min={1}
            max={40}
            step={0.5}
            value={zoom}
            onChange={(event) => setZoomKeepingCentre(Number(event.target.value))}
            aria-label="Zoom"
            className="w-28 accent-chalk"
          />
        </label>
      </div>

      <div className="flex items-end gap-2">
        <div
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
          onWheel={(event) => {
            if (!event.ctrlKey && !event.metaKey) return;
            event.preventDefault();
            setZoomKeepingCentre(zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15));
          }}
        >
          <div style={{ width: `${zoom * 100}%` }}>
            {/* The ruler: the finished video's seconds. Dragging along it scrubs. */}
            <div
              className="relative h-5 cursor-ew-resize select-none text-micro text-chalk-faint"
              onMouseDown={(event) => {
                drag.current = { kind: 'scrub' };
                onSeekOut(outAt(event.clientX));
              }}
            >
              {ticks.map((t) => (
                <span key={t} className="absolute -translate-x-1/2 font-mono" style={{ left: left(t) }}>
                  {timecode(t)}
                </span>
              ))}
            </div>

            <div
              ref={trackRef}
              className="relative h-16 select-none overflow-hidden rounded-md border border-hairline bg-slate-900"
            >
              <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
              {plan.map((placed, index) => {
                const { piece } = placed;
                const id = pieceId(piece);
                const seconds = placed.outEnd - placed.outStart;
                const selected = id === selectedId;
                const isCurrent = id === currentId;
                const source = piece.kind === 'clip' ? sources.get(piece.videoId) : undefined;
                // A seam where the original does not carry straight on: a
                // cut sits between this piece and the one before it.
                const previous = plan[index - 1]?.piece;
                const continues =
                  piece.kind === 'self' && previous?.kind === 'self' && Math.abs(previous.end - piece.start) < 0.002;
                return (
                  <div
                    key={id}
                    className={cx(
                      'group/piece absolute inset-y-0 overflow-hidden',
                      piece.kind === 'clip' ? 'bg-signal/25' : 'bg-chalk/[0.03]',
                      source && !source.available && 'bg-caution/25',
                      index > 0 && (continues ? 'border-l border-chalk/25' : 'border-l-2 border-dashed border-chalk/50'),
                      isCurrent && !selected && 'bg-chalk/[0.07]',
                      selected && 'ring-1 ring-inset ring-chalk/70',
                    )}
                    style={{ left: left(placed.outStart), width: width(seconds) }}
                    title={
                      piece.kind === 'clip'
                        ? `${source?.title ?? 'Clip'} · ${timecode(piece.start)} to ${timecode(piece.end)} of it · drag to move, drag the edges to trim`
                        : `${timecode(piece.start)} to ${timecode(piece.end)} of the original · drag to move, drag the edges to trim`
                    }
                  >
                    <button
                      type="button"
                      aria-label={piece.kind === 'clip' ? `Move ${source?.title ?? 'clip'}` : `Move the piece from ${timecode(piece.start)}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        drag.current = { kind: 'move', id, grabX: event.clientX, moved: false, outAtGrab: outAt(event.clientX) };
                      }}
                      className="absolute inset-0 cursor-grab px-2 text-left active:cursor-grabbing"
                      style={
                        source?.posterUrl
                          ? { backgroundImage: `url(${source.posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                          : undefined
                      }
                    >
                      {piece.kind === 'clip' ? <span className="absolute inset-0 bg-slate-950/55" /> : null}
                      <span className="relative block truncate pt-1 text-micro text-chalk">
                        {piece.kind === 'clip' ? (source?.available === false ? 'Unavailable' : (source?.title ?? 'Clip')) : ''}
                      </span>
                      <span className="relative block truncate font-mono text-micro tnum text-chalk-dim">
                        {seconds > 1.5 ? (piece.kind === 'clip' ? timecode(seconds) : `${timecode(piece.start)}–${timecode(piece.end)}`) : ''}
                      </span>
                    </button>
                    {(['start', 'end'] as const).map((edge) => (
                      <div
                        key={edge}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={edge === 'start' ? 'Trim the start' : 'Trim the end'}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onSelect(id);
                          drag.current = { kind: 'trim', id, edge };
                        }}
                        className={cx(
                          'absolute inset-y-0 w-1.5 cursor-col-resize transition-colors duration-120',
                          edge === 'start' ? 'left-0' : 'right-0',
                          piece.kind === 'clip' ? 'bg-signal/70 hover:bg-signal' : 'bg-chalk/20 hover:bg-chalk/70',
                          !selected && piece.kind === 'self' && 'opacity-0 group-hover/piece:opacity-100',
                        )}
                      />
                    ))}
                  </div>
                );
              })}
              <div className="pointer-events-none absolute inset-y-0 z-20 w-px bg-chalk" style={{ left: left(outTime) }}>
                <div className="absolute -left-1 -top-0.5 h-2 w-2 rotate-45 bg-chalk" />
              </div>
            </div>
          </div>
        </div>

        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={addDisabled}
            title={addDisabled ? `A recording can hold ${MAX_CLIPS} clips` : 'Add a piece of another recording onto the end'}
            className={cx(
              'flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-hairline',
              'text-micro text-chalk-dim transition-colors duration-120 hover:border-chalk/50 hover:bg-slate-900 hover:text-chalk',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            <PlusIcon size={16} />
            Add clip
          </button>
        ) : null}
      </div>

      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}
